import type { Filter, VisualSpec } from '@numbernode/contracts';

export function quoteIdentifier(value: string): string {
  if (!value.trim()) throw new Error('Identifier cannot be empty.');
  return `"${value.replaceAll('"', '""')}"`;
}

export function sqlLiteral(value: string | number | boolean | null): string {
  if (value === null) return 'NULL';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('Non-finite numbers are not valid SQL values.');
    return String(value);
  }
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  return `'${value.replaceAll("'", "''")}'`;
}

export function compileFilter(filter: Filter): string {
  const field = quoteIdentifier(filter.field);
  switch (filter.operator) {
    case 'is-null': return `${field} IS NULL`;
    case 'is-not-null': return `${field} IS NOT NULL`;
    case 'contains': return `CAST(${field} AS VARCHAR) ILIKE ${sqlLiteral(`%${String(filter.value ?? '')}%`)}`;
    case 'in': {
      const values = Array.isArray(filter.value) ? filter.value : [filter.value ?? ''];
      return `${field} IN (${values.map((value) => sqlLiteral(value)).join(', ')})`;
    }
    default:
      return `${field} ${filter.operator} ${sqlLiteral(filter.value as string | number | boolean)}`;
  }
}

export function aggregationSql(aggregation: VisualSpec['aggregation'], field?: string): string {
  if (aggregation === 'count') return 'COUNT(*)';
  if (!field) return 'COUNT(*)';
  const quoted = quoteIdentifier(field);
  switch (aggregation) {
    case 'none': return quoted;
    case 'count-distinct': return `COUNT(DISTINCT ${quoted})`;
    case 'avg': return `AVG(${quoted})`;
    case 'min': return `MIN(${quoted})`;
    case 'max': return `MAX(${quoted})`;
    case 'sum': return `SUM(${quoted})`;
    default: return `SUM(${quoted})`;
  }
}

export function compileVisualQuery(spec: VisualSpec): string {
  const select: string[] = [];
  const group: string[] = [];
  if (spec.chartType === 'map' && spec.latitude && spec.longitude) {
    select.push(`${quoteIdentifier(spec.latitude)} AS latitude`);
    select.push(`${quoteIdentifier(spec.longitude)} AS longitude`);
    if (spec.measure) select.push(`${aggregationSql(spec.aggregation, spec.measure)} AS value`);
    if (spec.aggregation !== 'none') {
      group.push(quoteIdentifier(spec.latitude), quoteIdentifier(spec.longitude));
    }
  } else {
    if (spec.dimension) {
      select.push(`${quoteIdentifier(spec.dimension)} AS dimension`);
      if (spec.aggregation !== 'none') group.push(quoteIdentifier(spec.dimension));
    }
    select.push(`${aggregationSql(spec.aggregation, spec.measure)} AS value`);
  }
  const where = spec.filters.length ? `\nWHERE ${spec.filters.map(compileFilter).join(' AND ')}` : '';
  const groupBy = group.length ? `\nGROUP BY ${group.join(', ')}` : '';
  const orderBy = spec.chartType === 'line' || spec.chartType === 'area' ? '\nORDER BY dimension' : '\nORDER BY value DESC';
  return `SELECT\n  ${select.join(',\n  ')}\nFROM ${quoteIdentifier(spec.tableName)}${where}${groupBy}${orderBy}\nLIMIT ${Math.max(1, Math.min(spec.limit, 100000))}`;
}

export function createViewSql(name: string, sourceSql: string): string {
  if (!/^\s*(select|with)\b/i.test(sourceSql)) throw new Error('A transformation must be a SELECT or WITH query.');
  return `CREATE OR REPLACE VIEW ${quoteIdentifier(name)} AS\n${sourceSql.replace(/;\s*$/, '')}`;
}
